import { Checkbox, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AdminTeamContributionsService } from '../../app/admin/team-contributions/service';
import { RootState } from '../../store/store';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { formatPrice } from '../functions/formatPrice';
import { handleError } from '../functions/handleError';
import { ITeamContributions } from '../interfaces/team-contributions/ITeamContributions';
import { darkTheme, lightTheme } from '../mui/theme';

export const useTeamContributionsColumns = (
  setSelectedItem: (teamContribution: ITeamContributions) => void,
  setShowDeleteModal: (show: boolean) => void,
  handleUpdateList: () => void,
): GridColDef[] => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('general.name'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{params?.row?.name ?? '-'}</div>,
    },
    {
      field: 'users',
      headerName: t('general.numberOfMembers'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return <div>{params?.row?.users.length}</div>;
      },
    },
    {
      field: 'price',
      headerName: t('expenses.price'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return <div>{formatPrice(params?.value ?? 0)}</div>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('general.createdAt'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { createdAt } = params?.row;
        return <div>{formatDate(createdAt)}</div>;
      },
    },
    {
      field: 'isFinished',
      headerName: t('general.isFinished'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div className={'d-flex justify-content-center'}>
            <Tooltip title={t('general.isFinished')}>
              <Checkbox
                checked={params?.row?.isFinished}
                onClick={async () => {
                  await AdminTeamContributionsService.update(
                    {
                      ...params.row,
                      isFinished: !params?.row?.isFinished,
                    },
                    Number(params?.row?.id),
                  )
                    .then(() => {
                      handleUpdateList();
                      enqueueSnackbar(
                        t('toasts.successfullyUpdated', {
                          title: params?.row?.name,
                        }),
                        { variant: 'success' },
                      );
                    })
                    .catch(handleError);
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 100,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '100%',
            }}
          >
            {/* Edit Icon */}
            <Tooltip title={t('general.edit')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  navigate(`/admin/team-contributions/${params?.row?.id}`);
                }}
              >
                <EditIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>

            {/* Delete Icon */}
            <Tooltip title={t('general.delete')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  setSelectedItem(params?.row);
                  setShowDeleteModal(true);
                }}
              >
                <TrashIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];
};
